import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getGradingOverview } from '../../services/studentService';
import { BELT_ORDER, getNextBelt } from '../../constants/belts';
import { beltAccent, plural, joinNatural, BeltCorrectionModal } from './gradingShared';

// Page 1 of 2 — the ladder, nothing else. An index into the per-belt lists (GradingBelt.jsx),
// not a place to read student rows: it must fit one screen at typical sizes.

export default function Grading() {
  const qc = useQueryClient();
  const [correctionTarget, setCorrectionTarget] = useState(null);
  const [showAllRanks, setShowAllRanks] = useState(false);

  const { data, isPending, error } = useQuery({
    queryKey: ['grading-overview'],
    queryFn: () => getGradingOverview().then(r => r.data.students),
  });

  const students = useMemo(() => data || [], [data]);

  const rungs = useMemo(() => {
    const byBelt = new Map(BELT_ORDER.map(b => [b, { belt: b, ready: 0, close: 0, notYet: 0, unknown: 0 }]));
    for (const s of students) {
      const rung = byBelt.get(s.currentBelt);
      if (!rung) continue;
      if (s.bucket === 'eligible') rung.ready++;
      else if (s.bucket === 'close') rung.close++;
      else if (s.bucket === 'notYet') rung.notYet++;
      else rung.unknown++;
    }
    return BELT_ORDER.map(b => {
      const r = byBelt.get(b);
      const headcount = r.ready + r.close + r.notYet + r.unknown;
      const parts = [];
      if (r.ready)   parts.push(plural(r.ready, 'ready'));
      if (r.close)   parts.push(plural(r.close, 'close'));
      if (r.notYet)  parts.push(`${r.notYet} not yet`);
      if (r.unknown) parts.push(`${r.unknown} unknown`);
      return { ...r, headcount, subline: parts.join(' · ') };
    });
  }, [students]);

  // Showing all 13 ranks pushes the real content off-screen when most are empty. Default view:
  // every OCCUPIED belt, plus exactly one empty rung above the highest occupied one — the belt
  // students are actually climbing toward. Everything else sits behind "Show all ranks".
  const visibleRungs = useMemo(() => {
    if (showAllRanks) return rungs;
    const occupiedIdx = rungs.reduce((max, r, i) => (r.headcount > 0 ? i : max), -1);
    return rungs.filter((r, i) => r.headcount > 0 || i === occupiedIdx + 1);
  }, [rungs, showAllRanks]);
  const hiddenRankCount = rungs.length - visibleRungs.length;

  const eligible = useMemo(() => students.filter(s => s.bucket === 'eligible'), [students]);
  const close    = useMemo(() => students.filter(s => s.bucket === 'close'), [students]);
  const unknown  = useMemo(() => students.filter(s => s.bucket === 'unknown'), [students]);

  const readyTargets = useMemo(() => {
    const targets = new Set(eligible.map(s => getNextBelt(s.currentBelt)).filter(Boolean));
    return BELT_ORDER.filter(b => targets.has(b));
  }, [eligible]);

  const invalidate = () => { setCorrectionTarget(null); qc.invalidateQueries({ queryKey: ['grading-overview'] }); };

  if (isPending) return <div className="p-4 text-sm text-subtle">Loading…</div>;
  if (error) return <div className="p-4 text-sm text-red-400">Failed to load grading overview.</div>;

  return (
    <div className="p-4 pb-10">
      <h1 className="text-lg font-semibold text-secondary">Grading</h1>

      <div className="flex gap-4 text-xs mt-2">
        <Link to="/admin/belt-exams" className="text-muted hover:text-secondary">Belt Exams</Link>
        <Link to="/admin/settings?section=attendance-rules" className="text-muted hover:text-secondary">Grading rules</Link>
      </div>

      {/* The one bold moment — generous room, nothing competing beside it. */}
      <div className="py-8">
        {eligible.length > 0 ? (
          <>
            <p className="text-3xl font-extrabold tracking-[-0.02em] leading-tight text-secondary">
              {plural(eligible.length, 'student')} ready to grade.
            </p>
            <p className="text-3xl font-extrabold tracking-[-0.02em] leading-tight text-secondary">
              {joinNatural(readyTargets)}.
            </p>
          </>
        ) : (
          <>
            <p className="text-3xl font-extrabold tracking-[-0.02em] leading-tight text-secondary">
              {close.length} close.
            </p>
            <p className="text-3xl font-extrabold tracking-[-0.02em] leading-tight text-secondary">
              None ready.
            </p>
          </>
        )}
      </div>

      <div className="flex flex-col-reverse">
        {visibleRungs.map((r) => {
          const empty = r.headcount === 0;
          return (
            <Link
              key={r.belt}
              to={`/admin/grading/${encodeURIComponent(r.belt)}`}
              className={`flex items-center justify-between gap-3 min-h-[44px] px-3.5 py-2 rounded-[0_10px_10px_0] border-l-[5px] transition-colors ${empty ? '' : 'bg-card hover:bg-nav-hover'}`}
              style={{ borderColor: beltAccent(r.belt) }}
            >
              <div className="min-w-0">
                <p className={`text-sm font-medium truncate ${empty ? 'text-subtle' : 'text-secondary'}`}>{r.belt}</p>
                {!empty && <p className="text-xs text-muted mt-0.5">{r.subline}</p>}
              </div>
              <span className="text-xl font-extrabold flex-shrink-0" style={{ color: empty ? undefined : beltAccent(r.belt) }}>
                <span className={empty ? 'text-subtle' : ''}>{empty ? '—' : r.headcount}</span>
              </span>
            </Link>
          );
        })}
        {/* Last in DOM order = top of the visual ladder (column-reverse puts later children
            higher up) — the toggle sits above the topmost visible rung, where the hidden
            higher ranks actually are. */}
        {hiddenRankCount > 0 && (
          <button
            onClick={() => setShowAllRanks(true)}
            className="text-xs font-medium text-muted hover:text-secondary text-left px-3.5 py-2 min-h-[44px]"
          >
            Show all ranks ({hiddenRankCount} more)
          </button>
        )}
      </div>

      {unknown.length > 0 && (
        <div className="pt-8">
          {unknown.map(s => (
            <div key={s.studentId} className="flex items-center justify-between gap-3 py-2.5 border-t border-divider">
              <span className="text-xs text-subtle">{s.name} ({s.currentBelt}) has no promotion date on file</span>
              <button onClick={() => setCorrectionTarget(s)} className="text-xs font-medium text-primary hover:underline flex-shrink-0 min-h-[44px]">
                Set baseline
              </button>
            </div>
          ))}
        </div>
      )}

      {correctionTarget && (
        <BeltCorrectionModal
          student={correctionTarget}
          onClose={() => setCorrectionTarget(null)}
          onSaved={invalidate}
        />
      )}
    </div>
  );
}
